import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ParallaxSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [visible, setVisible] = useState(false);
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const scrolled = -rect.top * 0.3;
      setOffset(scrolled);
      if (rect.top < window.innerHeight * 0.8) setVisible(true);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="relative h-[600px] md:h-[700px] overflow-hidden flex items-center">
      {/* Parallax BG */}
      <div
        className="absolute inset-0 w-full"
        style={{ transform: `translateY(${offset}px)`, willChange: 'transform' }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/c967fe28848cdda5f190b5fb03d637ce.jpg"
          alt="Modern glass design Las Vegas"
          className="w-full h-full object-cover object-center scale-110"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/60" />
        {/* Glass reflection */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 60%)' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <div className={`max-w-2xl transition-all duration-1000 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <span className="inline-block text-white/60 text-xs font-semibold uppercase tracking-widest border border-white/20 px-4 py-1.5 rounded-full mb-6">
            {isEs ? 'Diseño Premium' : 'Premium Design'}
          </span>
          <h2
            className="text-5xl md:text-6xl lg:text-7xl font-light text-white leading-tight mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {isEs ? 'Diseño Moderno de Vidrio.' : 'Modern Glass Design.'}<br />
            <span className="font-semibold italic">
              {isEs ? 'Construido para la Elegancia.' : 'Built for Elegance.'}
            </span>
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-lg">
            {isEs
              ? 'Cada instalación es una obra de arte. Vidrio templado de precisión, hardware premium y artesanía impecable — en cada proyecto.'
              : 'Every installation is a work of art. Precision tempered glass, premium hardware, and flawless craftsmanship — on every single project.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-3 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {isEs ? 'Ver Nuestros Servicios' : 'Explore Our Services'}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="tel:+17026042093"
              className="inline-flex items-center justify-center gap-3 border border-white/40 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              (702) 604-2093
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParallaxSection;
